import { ProjectEventListItem } from 'presentations/api/schema/project/project_event';
import { ViewerItem } from 'presentations/api/schema/user';
import { useContext, useEffect, useState } from 'react';
import {
  CalendarContext,
  setSchedules,
  clearSlots,
} from 'presentations/views/components/atoms/Calendar/reducer/CalendarContext';
import { Status } from 'domains/models/event/status';
import { CalendarEventAttendeeListItem } from 'presentations/api/schema/calendar_event';
import { CalendarSourceType } from 'presentations/api/schema/calendar_event/enum';
import { EventListItem } from 'presentations/api/schema/event';
import { Schedule, ScheduleType } from '../../atoms/Calendar/reducer';
import {
  findVideoServiceTypeFromText,
  findVideoServiceTypeFromUrl,
} from 'domains/models/calendar/videoServiceType';
import { getEventTitle } from 'presentations/views/helpers/event';
import { SourceType } from 'domains/models/event_source/sourceType';
import { ResponseStatus } from 'domains/models/event/responseStatus';

interface Props {
  viewer?: ViewerItem;
  events: ProjectEventListItem[];
  isLoading?: boolean;
}

export const useDashboardCalendar = (props: Props) => {
  const { state, dispatch } = useContext(CalendarContext);
  const [isShowingSelectedDates, setIsShowingSelectedDates] = useState<boolean>(
    false,
  );

  useEffect(() => {
    if (props.isLoading) {
      dispatch(setSchedules(null));
      return;
    }
    const schedules: Schedule[] = [];
    for (const userEvent of props.events) {
      for (const calendarEvents of userEvent.events) {
        const event: any = calendarEvents.event;
        let isRecurringEvent = false;
        if (calendarEvents.calendarSourceType === CalendarSourceType.Jicoo) {
          const e = event as EventListItem;
          if (
            ![Status.Open, Status.Reserved, Status.Request].includes(e.status)
          ) {
            continue;
          }
          isRecurringEvent = e.isRoutine;
        } else if (
          calendarEvents.calendarSourceType === CalendarSourceType.Integration
        ) {
          isRecurringEvent = event.isRecurringEvent;
        }
        const videoServices = [];
        if (event.url) {
          const videoServiceType = findVideoServiceTypeFromUrl(event.url);
          if (videoServiceType) {
            videoServices.push({
              url: event.url,
              videoServiceType,
            });
          }
        }
        if (event.description) {
          const matches = findVideoServiceTypeFromText(event.description);
          if (matches) {
            videoServices.push({
              url: matches.url,
              videoServiceType: matches.videoServiceType,
            });
          }
        }
        let name = event.name;
        if (
          event.sourceType === SourceType.EventType &&
          event.status !== Status.Reserved
        ) {
          name = getEventTitle(event);
        }
        let responseStatus = null;
        if (event.attendees) {
          const attendee = event.attendees.find(
            (a: CalendarEventAttendeeListItem) => a.isSelf === true,
          );
          if (attendee) responseStatus = attendee.responseStatus;
        }
        if (responseStatus === null && event.status === Status.Request) {
          responseStatus = ResponseStatus.NeedsAction;
        }
        schedules.push({
          scheduleType: ScheduleType.Event,
          uid: event.uid,
          name,
          startedAt: event.startedAt,
          endedAt: event.endedAt,
          timeZone: event.timeZone,
          transparency: event.transparency,
          color: event.color,
          isAllDay: event.isAllDay,
          userId: userEvent.userId,
          calendarId: event.calendarId || null,
          eventId: event.uid,
          url: event.url,
          status: event.status,
          responseStatus,
          providerType: event.providerType,
          description: event.description,
          address: event.address,
          calendarSource: Number(calendarEvents.calendarSourceType),
          isRecurringEvent,
          behavior: event.behavior,
        });
      }
    }
    dispatch(setSchedules(schedules));
  }, [props.events, props.isLoading]);

  const onClickShowSelectedDates = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsShowingSelectedDates(true);
  };

  const onCloseShowSelectedDates = () => {
    setIsShowingSelectedDates(false);
  };

  const onClearSelectedDates = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsShowingSelectedDates(false);
    dispatch(clearSlots());
  };

  return {
    state: {
      isShowingSelectedDates,
      timeZone: state.timeZone,
      isReady: state.isReady,
      startedAt: state.startedAt,
      creatingEvent: state.creatingEvent,
    },
    actions: {
      onClearSelectedDates,
      onClickShowSelectedDates,
      onCloseShowSelectedDates,
    },
  };
};
